// FA Lama / cutover — satu halaman, bagian per peran (Q39–Q45, K163, K167).
//
// ⛔ TIGA HAL YANG WAJIB (Owner, 14 Sep 2026 — UAT menunjukkan ketiganya
//    yang memakan waktu):
//   1. NOL `.catch(() => setX([]))`. Pemuatan yang gagal DIKATAKAN di layar
//      (`galat[...]`), bukan jadi dropdown kosong yang tampak sah
//      (`CATCH-MENELAN-GALAT-JADI-DAFTAR-KOSONG-01`, 19 kemunculan).
//   2. NOL uuid di kolom nama: customer, cabang ambil, tahap tampil sebagai
//      NAMA; kalau namanya nol ada → "nama nol tersedia"
//      (`RINCIAN-RETUR-…-UUID`, 4 kemunculan).
//   3. NOL objek ke toast: `pesan()` selalu mengembalikan string
//      (`WO-DETAIL-403-LAYAR-CRASH-OBJEK-KE-TOAST-01`).
//
// ⚠ Kepala halaman menampilkan SISA JAM jendela, bukan hanya hidup/mati —
//   CS yang melihat "hidup" tanpa tahu tinggal 2 jam akan menunda.
// ⛔ `total_amount_idr` DIKETIK CS, BUKAN dihitung dari daftar harga —
//   layar mengatakannya terang di sebelah ruasnya.
//
// Peran (dari service `fa_lama`): OWNER buka jendela · KEPALA_CABANG tutup +
// rekonsiliasi · CS isi FA (+ angka pembayaran, K163) · ADMIN_PRODUKSI tahap
// terakhir · FINANCE verifikasi (nol angka). Semua tombol tulis MATI saat
// jendela mati — dan kalimat gerbangnya tertulis, bukan tombol yang hilang.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import CustomerSearchSelect from "@/components/etams/CustomerSearchSelect";
import { fetchProductionStages, stageLabel } from "@/lib/productionStages";
import { formatIDR, formatDate, formatDateTime } from "@/lib/format";
import { Link } from "react-router-dom";
import { apiErrorMessage } from "@/lib/inventoryApi";
import Hint from "@/components/etams/Hint";
import { FA_ORDER_STATUS } from "@/components/etams/StatusBadge";
import { useDaftar } from "@/hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "@/components/etams/PalangDaftar";

//: Cermin `URUT_SAH_FA_LAMA` di backend/routers/fa_lama.py.
const URUT_SAH_FA_LAMA = ["created_at", "fa_number", "nomor_fa_lama", "tanggal_fa_asli", "order_status"];

// ---------------------------------------------------------------- util
/** Pesan galat SELALU string — objek `{error, message}` diratakan. */
function pesan(err, fallback = "Terjadi kesalahan.") {
  return apiErrorMessage(err, fallback);
}

// Nama sumber pemuatan yang tampil di kotak galat (bukan kunci kode).
const SUMBER_GALAT = { regions: "Wilayah", tahap: "Tahap produksi", stores: "Cabang", jendela: "Jendela", daftar: "Daftar FA lama" };

/** Nama dari embed; nol pernah uuid. */
function nama(obj, kalauKosong = "nama tidak tersedia") {
  const n = obj && typeof obj === "object" ? obj.name : null;
  return typeof n === "string" && n.trim() ? n : kalauKosong;
}

const METODE = [
  { v: "AMBIL_DI_CABANG", l: "Diambil sendiri di cabang" },
  { v: "TITIP_AMBIL_CABANG", l: "Titip & ambil di cabang lain (sewilayah)" },
  { v: "KIRIM_KE_ALAMAT", l: "Kirim ke alamat" },
];
const metodeLabel = (v) => (v ? (METODE.find((m) => m.v === v)?.l || v) : "belum ditanyakan");

const STATUS_LABEL = {
  MENUNGGU_CS: "Menunggu CS",
  MENUNGGU_PRODUKSI: "Menunggu Admin Produksi",
  MENUNGGU_FINANCE: "Menunggu verifikasi Finance",
  LENGKAP: "Lengkap",
};

function sisaJam(jendela) {
  if (!jendela?.berlaku_sampai) return null;
  const ms = new Date(jendela.berlaku_sampai).getTime() - Date.now();
  return ms / 3_600_000;
}

const sel = "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground";

// ---------------------------------------------------------------- halaman
export default function FaLama() {
  const { profile, roleCode, isOwner, isKepalaCabang, isCS } = useAuth();
  const isProduksi = roleCode === "ADMIN_PRODUKSI";
  const isFinance = roleCode === "FINANCE";
  // K206 / VONIS-HAK-BACA-ISI-FA: isi FA (customer + telepon, alamat, jumlah, catatan) hanya Owner/KC/CS. Admin
  // Produksi menerima nomor + status + janji + tahap; Finance + nominal (haknya). Kolom mengikuti yang dikirim backend.
  const isiLengkap = ["OWNER", "KEPALA_CABANG", "CS"].includes(roleCode);
  // ⛔ Pemilih region untuk peran LINTAS REGION (profil.region_id NULL karena
  // is_region_unbound, bukan belum diisi). Finance memilih region → jendela
  // + daftar region itu dimuat → tombol Verifikasi hidup. Tanpa ini Finance
  // — satu-satunya pemverifikasi — melihat "region nol dikenal" dan tombol
  // mati (`FA-LAMA-DAFTAR-FINANCE-403-PROFIL-NOL-LENGKAP-01`, UAT PJ-14).
  const pilihRegion = isOwner || isFinance;

  // Region yang layar ini bicarakan: profil (staf), atau pilihan (Owner).
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState(profile?.region_id || "");
  const [jendela, setJendela] = useState(null);
  const [hidup, setHidup] = useState(false);
  const [daftar, setDaftar] = useState([]);
  const [totalDaftar, setTotalDaftar] = useState(0);
  const [totalWilayah, setTotalWilayah] = useState(0);
  const [stages, setStages] = useState([]);
  const [stores, setStores] = useState([]);
  const [galat, setGalat] = useState({});   // sumber → kalimat; nol pernah ditelan
  const [sibuk, setSibuk] = useState(false);
  const [detik, setDetik] = useState(0);     // pemicu hitung ulang sisa jam

  const catatGalat = (k, e) => setGalat((g) => ({ ...g, [k]: pesan(e) }));
  const hapusGalat = (k) => setGalat((g) => { const n = { ...g }; delete n[k]; return n; });

  useEffect(() => {
    const t = setInterval(() => setDetik((d) => d + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!pilihRegion) return;
    api.get("/regions")
      .then((r) => { setRegions(r.data?.items || r.data || []); hapusGalat("regions"); })
      .catch((e) => catatGalat("regions", e));
  }, [pilihRegion]);

  useEffect(() => {
    fetchProductionStages()
      .then((s) => { setStages(s || []); hapusGalat("tahap"); })
      .catch((e) => catatGalat("tahap", e));
    api.get("/stores")
      .then((r) => { setStores(r.data?.items || r.data || []); hapusGalat("stores"); })
      .catch((e) => catatGalat("stores", e));
  }, []);

  const muatJendela = useCallback(async () => {
    if (!regionId) return;
    try {
      const r = await api.get(`/fa-lama/jendela/${regionId}`);
      setJendela(r.data?.jendela || null);
      setHidup(Boolean(r.data?.hidup));
      hapusGalat("jendela");
    } catch (e) { setJendela(null); setHidup(false); catatGalat("jendela", e); }
  }, [regionId]);

  // Daftar FA lama — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
  // Dulu: satu tarikan 500 baris lalu disaring wilayah di BROWSER (FA lama ke-501 hilang
  // tanpa tanda). Kini wilayah, cari (nomor sistem / nomor lama / customer), urut, dan
  // halaman di server. Wilayah staf tetap dikunci server ke profilnya; `region_id` hanya
  // berlaku untuk Owner/Finance. Angka "Sistem menghitung N" di tutup jendela memakai
  // `total_wilayah` — jumlah seluruh FA lama wilayah itu, tidak ikut menyusut saat mencari.
  const pola = useDaftar(daftar, {
    server: { total: totalDaftar, urutSah: URUT_SAH_FA_LAMA },
    urutAwal: { kunci: "created_at", arah: "desc" },
  });
  const { cari, limit, offset, sort, arah } = pola.paramServer;

  const muatDaftar = useCallback(async () => {
    try {
      const r = await api.get("/fa-lama", {
        params: { ...(regionId && { region_id: regionId }), ...(cari && { q: cari }), limit, offset, sort, arah },
      });
      const items = Array.isArray(r.data?.items) ? r.data.items : [];
      setDaftar(items);
      setTotalDaftar(typeof r.data?.total === "number" ? r.data.total : items.length);
      setTotalWilayah(typeof r.data?.total_wilayah === "number" ? r.data.total_wilayah : items.length);
      hapusGalat("daftar");
    } catch (e) { setDaftar([]); setTotalDaftar(0); catatGalat("daftar", e); }
  }, [regionId, cari, limit, offset, sort, arah]);

  useEffect(() => { muatJendela(); }, [muatJendela]);
  useEffect(() => { muatDaftar(); }, [muatDaftar]);

  const jam = useMemo(() => sisaJam(jendela), [jendela, detik]); // eslint-disable-line react-hooks/exhaustive-deps
  const regionNama = useMemo(() => {
    if (profile?.region?.name && profile.region.id === regionId) return profile.region.name;
    return nama(regions.find((r) => r.id === regionId), "wilayah tidak dikenali");
  }, [profile, regions, regionId]);
  const storeSewilayah = useMemo(
    () => stores.filter((s) => s.region_id === regionId), [stores, regionId]);

  // ---------------------------------------------------------------- aksi
  async function jalankan(kunci, fn, sukses) {
    setSibuk(true);
    try {
      await fn();
      if (sukses) toast.success(sukses);
      await Promise.all([muatJendela(), muatDaftar()]);
      return true;
    } catch (e) {
      toast.error(pesan(e));
      return false;
    } finally { setSibuk(false); }
  }

  const bukaJendela = () => jalankan("buka",
    () => api.post("/fa-lama/jendela/buka", { region_id: regionId }),
    "Jendela FA lama dibuka — 48 jam, mati sendiri.");

  return (
    <div className="p-6 space-y-6" data-testid="fa-lama-page">
      <div>
        <h1 className="text-2xl font-bold">
          FA Lama — pesanan yang sudah berjalan sebelum sistem{" "}
          <Hint>Hanya bisa diisi selama jendela 48 jam yang dibuka Owner, lalu ditutup Kepala Cabang dengan pencocokan jumlah. CS mengisi data, harga, dan angka pembayaran; Admin Produksi tahap terakhir; Finance memverifikasi.</Hint>
        </h1>
      </div>

      {/* ⚠ galat pemuatan DIKATAKAN, satu baris per sumber */}
      {Object.keys(galat).length > 0 && (
        <Card className="p-3 border-destructive/50 bg-destructive/5 text-sm" data-testid="fa-lama-galat">
          <div className="font-semibold text-destructive mb-1">Ada yang gagal dimuat — layar ini tidak menyembunyikannya:</div>
          <ul className="list-disc ml-5 space-y-0.5">
            {Object.entries(galat).map(([k, v]) => <li key={k}><b>{SUMBER_GALAT[k] || "Data"}</b>: {v}</li>)}
          </ul>
        </Card>
      )}

      {/* ---------------------------------------------------------- kepala: jendela */}
      <Card className="p-4 space-y-3" data-testid="fa-lama-jendela">
        <div className="flex flex-wrap items-center gap-3">
          <div className="font-semibold">Jendela wilayah:</div>
          {pilihRegion ? (
            <select className={sel + " max-w-xs"} value={regionId} onChange={(e) => { setRegionId(e.target.value); pola.setHalaman(1); }}
                    data-testid="fa-lama-region">
              <option value="">— pilih wilayah —</option>
              {regions.map((r) => <option key={r.id} value={r.id}>{nama(r)}</option>)}
            </select>
          ) : (
            <Badge variant="outline">{regionNama}</Badge>
          )}
          {regionId && (
            hidup ? (
              <Badge className="bg-success text-success-foreground" data-testid="fa-lama-jendela-hidup">
                HIDUP · sisa {jam != null ? (jam >= 1 ? `${Math.floor(jam)} jam ${Math.round((jam % 1) * 60)} menit` : `${Math.max(0, Math.round(jam * 60))} menit`) : "—"}
              </Badge>
            ) : (
              <Badge variant="destructive" data-testid="fa-lama-jendela-mati">
                {jendela?.ditutup_at ? "TERTUTUP (sudah direkonsiliasi KC)"
                  : jendela ? "KEDALUWARSA (48 jam habis — belum ditutup KC)"
                  : "BELUM ADA JENDELA"}
              </Badge>
            )
          )}
        </div>
        {jendela && (
          <div className="text-xs text-muted-foreground">
            Dibuka {formatDateTime(jendela.dibuka_at)} · berlaku sampai {formatDateTime(jendela.berlaku_sampai)}
            {jendela.ditutup_at && <> · ditutup {formatDateTime(jendela.ditutup_at)} · manual {jendela.jumlah_manual} vs sistem {jendela.jumlah_sistem} → {jendela.jumlah_manual === jendela.jumlah_sistem ? "COCOK" : "TIDAK COCOK"}</>}
          </div>
        )}
        {isFinance && !regionId && (
          <div className="text-sm text-muted-foreground" data-testid="fa-lama-finance-pilih-region">
            Pilih wilayah untuk melihat FA lama yang menunggu verifikasi — jendela berlaku per wilayah.
          </div>
        )}
        {!hidup && regionId && (
          <div className="text-sm text-warning-soft-foreground">
            Jendela mati: seluruh isian FA lama untuk wilayah ini <b>ditolak sistem</b>.
            {isOwner && !jendela && " Buka jendela di bawah."}
            {isOwner && jendela && " Satu wilayah satu jendela — yang lama sudah ada; jalur ini mati permanen sesudah ditutup."}
          </div>
        )}
        {isOwner && regionId && !jendela && (
          <Button onClick={bukaJendela} disabled={sibuk} data-testid="fa-lama-buka">Buka jendela 48 jam untuk {regionNama}</Button>
        )}
        {isKepalaCabang && jendela && !jendela.ditutup_at && (
          <TutupJendela regionId={regionId} jumlahSistem={totalWilayah} sibuk={sibuk} jalankan={jalankan} />
        )}
      </Card>

      {/* ---------------------------------------------------------- CS */}
      {isCS && (
        <FormCS regionId={regionId} hidup={hidup} stores={storeSewilayah} sibuk={sibuk}
                jalankan={jalankan} galatStores={galat.stores} />
      )}

      {/* ---------------------------------------------------------- daftar + aksi peran */}
      <Card className="p-4 space-y-3" data-testid="fa-lama-daftar">
        <div className="flex items-center justify-between">
          <div className="font-semibold">FA lama wilayah ini ({totalWilayah})</div>
          <Button variant="outline" size="sm" onClick={() => { muatJendela(); muatDaftar(); }}>Muat ulang</Button>
        </div>
        {galat.daftar ? (
          <div className="text-sm text-destructive">Daftar gagal dimuat: {galat.daftar}</div>
        ) : totalWilayah === 0 && !pola.cari.trim() ? (
          <div className="text-sm text-muted-foreground">Belum ada FA lama di wilayah ini.</div>
        ) : (
          <>
            <PalangDaftar daftar={pola} placeholder={isiLengkap ? "Cari nomor sistem / nomor lama / customer" : "Cari nomor sistem / nomor lama"} testId="fa-lama-palang" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <ThUrut daftar={pola} kunci="fa_number" className="py-1 pr-3">No. sistem</ThUrut>
                    <ThUrut daftar={pola} kunci="nomor_fa_lama" className="pr-3">No. lama</ThUrut>
                    {isiLengkap && <><th className="pr-3">Customer</th><th className="pr-3">Jumlah</th></>}
                    {(isiLengkap || isFinance) && <th className="pr-3">Total · masuk</th>}
                    <th className="pr-3">Tahap terakhir</th>
                    <th className="pr-3">Kelengkapan</th>
                    <ThUrut daftar={pola} kunci="order_status" className="pr-3">Status FA</ThUrut><th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pola.tampil.length === 0 && (
                    <tr><td colSpan={isiLengkap ? 9 : isFinance ? 7 : 6} className="py-3 text-muted-foreground">Tidak ada FA lama yang cocok dengan pencarian ini.</td></tr>
                  )}
                  {pola.tampil.map((fa) => (
                    <BarisFA key={fa.id} fa={fa} stages={stages} hidup={hidup} sibuk={sibuk} jalankan={jalankan}
                             isProduksi={isProduksi} isFinance={isFinance} isiLengkap={isiLengkap} galatTahap={galat.tahap} />
                  ))}
                </tbody>
              </table>
            </div>
            <Paginasi daftar={pola} testId="fa-lama-paginasi" />
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- KC tutup
function TutupJendela({ regionId, jumlahSistem, sibuk, jalankan }) {
  const [manual, setManual] = useState("");
  const [catatan, setCatatan] = useState("");
  const [hasil, setHasil] = useState(null);
  const kirim = async () => {
    if (manual === "" || Number(manual) < 0) { toast.error("Isi jumlah FA menurut catatan manual cabang (≥ 0)."); return; }
    let jawab = null;
    const ok = await jalankan("tutup", async () => {
      const r = await api.post("/fa-lama/jendela/tutup", {
        region_id: regionId, jumlah_manual: Number(manual), catatan: catatan || null });
      jawab = r.data;
    }, null);
    if (ok && jawab) {
      setHasil(jawab);
      toast[jawab.cocok ? "success" : "warning"](
        jawab.cocok ? `Jendela ditutup. Manual ${jawab.jumlah_manual} = sistem ${jawab.jumlah_sistem} — COCOK.`
                    : `Jendela ditutup. Manual ${jawab.jumlah_manual} ≠ sistem ${jawab.jumlah_sistem} — TIDAK COCOK, ada order yang terlewat atau kelebihan.`);
    }
  };
  return (
    <div className="border-t pt-3 space-y-2" data-testid="fa-lama-tutup">
      <div className="text-sm font-medium">Tutup jendela + pencocokan jumlah (Kepala Cabang)</div>
      <div className="text-xs text-muted-foreground">
        Sistem menghitung <b>{jumlahSistem}</b> FA lama masuk. Ketik jumlah menurut <b>daftar manual cabang</b> —
        angka pembanding tidak boleh disalin dari angka sistem. Menutup = jalur FA lama <b>mati permanen</b> untuk wilayah ini.
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-xs">Jumlah menurut catatan manual
          <Input type="number" min="0" value={manual} onChange={(e) => setManual(e.target.value)} className="w-40" data-testid="fa-lama-jumlah-manual" /></label>
        <label className="text-xs flex-1 min-w-[240px]">Catatan (opsional)
          <Input value={catatan} onChange={(e) => setCatatan(e.target.value)} /></label>
        <Button variant="destructive" onClick={kirim} disabled={sibuk} data-testid="fa-lama-tutup-btn">Tutup jendela</Button>
      </div>
      {hasil && <div className="text-sm">Hasil: manual {hasil.jumlah_manual} · sistem {hasil.jumlah_sistem} · {hasil.cocok ? "COCOK" : "TIDAK COCOK"}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- CS form
const KOSONG = {
  customer: null, nomor_fa_lama: "", tanggal_fa_asli: "", janji_lama_date: "",
  package_type: "REGULER", total_qty_atasan: "", total_qty_celana: "0",
  total_amount_idr: "", tanda_jadi_amount_idr: "0", dp_amount_idr: "0", pelunasan_amount_idr: "0",
  metode_pengiriman: "", cabang_ambil_id: "", alamat_kirim: "", nama_team: "", notes: "",
};

function FormCS({ regionId, hidup, stores, sibuk, jalankan, galatStores }) {
  const [f, setF] = useState(KOSONG);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const angka = (v) => (v === "" || v == null ? 0 : Number(v));
  const masuk = angka(f.tanda_jadi_amount_idr) + angka(f.dp_amount_idr) + angka(f.pelunasan_amount_idr);
  const total = angka(f.total_amount_idr);

  const kirim = async () => {
    const wajib = [
      [f.customer?.id, "Customer"], [f.nomor_fa_lama.trim(), "Nomor FA lama"],
      [f.tanggal_fa_asli, "Tanggal FA asli"], [f.janji_lama_date, "Tanggal janji ke customer"],
      [f.total_qty_atasan !== "", "Jumlah atasan"], [f.total_amount_idr !== "", "Total harga yang disepakati"],
    ];
    const kurang = wajib.filter(([v]) => !v).map(([, n]) => n);
    if (kurang.length) { toast.error("Wajib diisi: " + kurang.join(", ") + "."); return; }
    if (f.metode_pengiriman === "TITIP_AMBIL_CABANG" && !f.cabang_ambil_id) {
      toast.error("Titip-ambil wajib menyebut cabang tempat customer mengambil barangnya."); return;
    }
    const body = {
      customer_id: f.customer.id, nomor_fa_lama: f.nomor_fa_lama.trim(),
      tanggal_fa_asli: f.tanggal_fa_asli, janji_lama_date: f.janji_lama_date,
      package_type: f.package_type,
      total_qty_atasan: angka(f.total_qty_atasan), total_qty_celana: angka(f.total_qty_celana),
      total_amount_idr: total, tanda_jadi_amount_idr: angka(f.tanda_jadi_amount_idr),
      dp_amount_idr: angka(f.dp_amount_idr), pelunasan_amount_idr: angka(f.pelunasan_amount_idr),
      metode_pengiriman: f.metode_pengiriman || null,
      cabang_ambil_id: f.metode_pengiriman === "TITIP_AMBIL_CABANG" ? f.cabang_ambil_id : null,
      alamat_kirim: f.alamat_kirim || null, nama_team: f.nama_team || null, notes: f.notes || null,
    };
    let nomor = null;
    const ok = await jalankan("buat", async () => { const r = await api.post("/fa-lama", body); nomor = r.data?.fa_number; }, null);
    if (ok) { toast.success(`FA lama tercatat sebagai ${nomor || "(nomor tidak terbaca)"} — menunggu Admin Produksi.`); setF(KOSONG); }
  };

  return (
    <Card className="p-4 space-y-4" data-testid="fa-lama-form-cs">
      <div>
        <div className="font-semibold">Catat FA lama (CS)</div>
        <div className="text-xs text-muted-foreground">
          Customer harus <b>sudah ada</b> — kalau belum, <Link className="underline" to="/customers">tambah di Customer</Link> dulu, lalu kembali.
        </div>
      </div>
      {!hidup && <div className="text-sm text-warning-soft-foreground">Jendela mati — formulir ini tidak bisa dikirim.</div>}

      <div className="grid md:grid-cols-2 gap-3">
        <label className="text-xs space-y-1 md:col-span-2">Customer <span className="text-destructive">*</span>
          <CustomerSearchSelect value={f.customer} onChange={(c) => set("customer", c)} disabled={!hidup} testId="fa-lama-customer" />
        </label>
        <label className="text-xs space-y-1">Nomor FA lama (dari catatan cabang) <span className="text-destructive">*</span>
          <Input value={f.nomor_fa_lama} onChange={(e) => set("nomor_fa_lama", e.target.value)} disabled={!hidup} data-testid="fa-lama-nomor" /></label>
        <label className="text-xs space-y-1">Nama tim (opsional)
          <Input value={f.nama_team} onChange={(e) => set("nama_team", e.target.value)} disabled={!hidup} /></label>
        <label className="text-xs space-y-1">Tanggal FA asli dibuat <span className="text-destructive">*</span>
          <Input type="date" value={f.tanggal_fa_asli} onChange={(e) => set("tanggal_fa_asli", e.target.value)} disabled={!hidup} data-testid="fa-lama-tgl-asli" /></label>
        <label className="text-xs space-y-1">Tanggal janji yang sudah disampaikan ke customer <span className="text-destructive">*</span>
          <Input type="date" value={f.janji_lama_date} onChange={(e) => set("janji_lama_date", e.target.value)} disabled={!hidup} data-testid="fa-lama-janji" /></label>
        <label className="text-xs space-y-1">Paket <span className="text-destructive">*</span>
          <select className={sel} value={f.package_type} onChange={(e) => set("package_type", e.target.value)} disabled={!hidup}>
            <option value="REGULER">Reguler</option><option value="EXPRESS">Express</option><option value="SUPER_EXPRESS">Super Express</option>
          </select></label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs space-y-1">Jumlah atasan <span className="text-destructive">*</span>
            <Input type="number" min="0" value={f.total_qty_atasan} onChange={(e) => set("total_qty_atasan", e.target.value)} disabled={!hidup} data-testid="fa-lama-qty-atasan" /></label>
          <label className="text-xs space-y-1">Jumlah celana (0 = atasan saja)
            <Input type="number" min="0" value={f.total_qty_celana} onChange={(e) => set("total_qty_celana", e.target.value)} disabled={!hidup} /></label>
        </div>

        <div className="md:col-span-2 rounded-lg border border-warning-soft-border bg-warning-soft p-3 space-y-2">
          <label className="text-xs space-y-1 block">Total harga yang disepakati (Rp) <span className="text-destructive">*</span>
            <Input type="number" min="0" value={f.total_amount_idr} onChange={(e) => set("total_amount_idr", e.target.value)} disabled={!hidup} data-testid="fa-lama-total" /></label>
          <div className="text-xs text-warning-soft-foreground">
            ⚠ <b>Angka ini DIKETIK, bukan dihitung sistem.</b> FA lama tidak lewat daftar harga, jadi sistem tidak
            mencocokkannya. Tulis persis yang disepakati dengan customer dulu.
          </div>
        </div>

        <div className="md:col-span-2 rounded-lg border p-3 space-y-2">
          <div className="text-xs font-medium">Pembayaran yang sudah masuk (diketik CS, diverifikasi Finance)</div>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs space-y-1">Tanda jadi (Rp)
              <Input type="number" min="0" value={f.tanda_jadi_amount_idr} onChange={(e) => set("tanda_jadi_amount_idr", e.target.value)} disabled={!hidup} data-testid="fa-lama-tj" /></label>
            <label className="text-xs space-y-1">DP (Rp)
              <Input type="number" min="0" value={f.dp_amount_idr} onChange={(e) => set("dp_amount_idr", e.target.value)} disabled={!hidup} data-testid="fa-lama-dp" /></label>
            <label className="text-xs space-y-1">Pelunasan (Rp)
              <Input type="number" min="0" value={f.pelunasan_amount_idr} onChange={(e) => set("pelunasan_amount_idr", e.target.value)} disabled={!hidup} data-testid="fa-lama-pel" /></label>
          </div>
          <div className="text-xs text-muted-foreground">
            Masuk {formatIDR(masuk)} dari total {formatIDR(total)} →{" "}
            {total > 0 && masuk >= total ? <b>LUNAS</b> : <b>belum lunas (sisa {formatIDR(Math.max(0, total - masuk))})</b>}.
            Status FA ditentukan dari angka ini saat Finance memverifikasi.
          </div>
        </div>

        <label className="text-xs space-y-1">Metode pengiriman (kesepakatan lama; boleh dikosongkan = belum ditanyakan)
          <select className={sel} value={f.metode_pengiriman} onChange={(e) => set("metode_pengiriman", e.target.value)} disabled={!hidup} data-testid="fa-lama-metode">
            <option value="">— belum ditanyakan —</option>
            {METODE.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select></label>
        {f.metode_pengiriman === "TITIP_AMBIL_CABANG" && (
          <label className="text-xs space-y-1">Cabang tempat ambil (sewilayah) <span className="text-destructive">*</span>
            {galatStores ? <div className="text-destructive">Daftar cabang gagal dimuat: {galatStores}</div> : (
              <select className={sel} value={f.cabang_ambil_id} onChange={(e) => set("cabang_ambil_id", e.target.value)} disabled={!hidup} data-testid="fa-lama-cabang">
                <option value="">— pilih cabang —</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{nama(s)}</option>)}
              </select>)}
          </label>
        )}
        {f.metode_pengiriman === "KIRIM_KE_ALAMAT" && (
          <label className="text-xs space-y-1">Alamat kirim
            <Input value={f.alamat_kirim} onChange={(e) => set("alamat_kirim", e.target.value)} disabled={!hidup} /></label>
        )}
        <label className="text-xs space-y-1 md:col-span-2">Catatan (opsional — sebaran size, lengan, nameset, hal lain yang belum punya ruas)
          <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} disabled={!hidup} rows={2} /></label>
      </div>
      <div className="flex justify-end">
        <Button onClick={kirim} disabled={!hidup || sibuk} data-testid="fa-lama-simpan">Catat FA lama</Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- baris + aksi
function BarisFA({ fa, stages, hidup, sibuk, jalankan, isProduksi, isFinance, isiLengkap, galatTahap }) {
  const [tahap, setTahap] = useState("");
  const [catatan, setCatatan] = useState("");
  const masuk = (fa.tanda_jadi_amount_idr || 0) + (fa.dp_amount_idr || 0) + (fa.pelunasan_amount_idr || 0);
  const tahapNama = fa.tahap_terakhir_lama ? stageLabel(stages, fa.tahap_terakhir_lama) : "—";

  const isiProduksi = () => {
    if (!tahap) { toast.error("Pilih tahap terakhir yang sudah dicapai barang ini."); return; }
    return jalankan("produksi", () => api.patch(`/fa-lama/${fa.id}/produksi`, { stage_code_terakhir: tahap, catatan: catatan || null }),
      `${fa.fa_number}: tahap terakhir ${stageLabel(stages, tahap)} tersimpan — menunggu Finance.`);
  };
  const verifikasi = () => jalankan("verifikasi", () => api.post(`/fa-lama/${fa.id}/verifikasi`, { catatan: catatan || null }),
    `${fa.fa_number}: angka pembayaran diverifikasi — FA lama LENGKAP.`);

  return (
    <tr className="border-t align-top" data-testid={`fa-lama-baris-${fa.fa_number}`}>
      <td className="py-2 pr-3 font-mono text-xs">{isiLengkap ? <Link className="underline" to={`/fa-orders/${fa.id}`}>{fa.fa_number}</Link> : fa.fa_number}<br /><Badge variant="outline" className="mt-1">Order Lama</Badge></td>
      <td className="pr-3">{fa.nomor_fa_lama}</td>
      {isiLengkap && (
        <>
          <td className="pr-3">{nama(fa.customer)}{fa.customer?.phone ? <div className="text-xs text-muted-foreground">{fa.customer.phone}</div> : null}
            <div className="text-xs text-muted-foreground">{metodeLabel(fa.metode_pengiriman)}{fa.metode_pengiriman === "TITIP_AMBIL_CABANG" ? ` · ${nama(fa.cabang_ambil, "cabang: nama tidak tersedia")}` : ""}</div></td>
          <td className="pr-3">{fa.total_qty_atasan} atasan{fa.total_qty_celana ? ` + ${fa.total_qty_celana} celana` : ""}</td>
        </>
      )}
      {(isiLengkap || isFinance) && (
        <td className="pr-3">{formatIDR(fa.total_amount_idr)}<div className="text-xs text-muted-foreground">masuk {formatIDR(masuk)}</div></td>
      )}
      <td className="pr-3">{galatTahap ? <span className="text-destructive text-xs">master tahap gagal dimuat</span> : tahapNama}</td>
      <td className="pr-3"><Badge variant={fa.status_fa_lama === "LENGKAP" ? "default" : "secondary"}>{STATUS_LABEL[fa.status_fa_lama] || fa.status_fa_lama}</Badge>
        <div className="text-xs text-muted-foreground">janji {fa.janji_lama_date ? formatDate(fa.janji_lama_date) : "—"}</div></td>
      <td className="pr-3 text-xs">{FA_ORDER_STATUS[fa.order_status]?.l || "—"}{fa.layak_wo ? <div className="text-success">layak WO (jalur FA lama)</div> : null}</td>
      <td className="min-w-[220px]">
        {isProduksi && fa.status_fa_lama === "MENUNGGU_PRODUKSI" && (
          <div className="space-y-1">
            {galatTahap ? <div className="text-destructive text-xs">Master tahap gagal dimuat: {galatTahap}</div> : (
              <select className={sel} value={tahap} onChange={(e) => setTahap(e.target.value)} disabled={!hidup} data-testid={`fa-lama-tahap-${fa.fa_number}`}>
                <option value="">— tahap terakhir yang dicapai —</option>
                {stages.map((s) => <option key={s.code} value={s.code}>{s.name || s.code}</option>)}
              </select>)}
            <Input placeholder="Catatan (opsional)" value={catatan} onChange={(e) => setCatatan(e.target.value)} disabled={!hidup} />
            <Button size="sm" onClick={isiProduksi} disabled={!hidup || sibuk} data-testid={`fa-lama-isi-produksi-${fa.fa_number}`}>Simpan tahap</Button>
          </div>
        )}
        {isFinance && fa.status_fa_lama === "MENUNGGU_FINANCE" && (
          <div className="space-y-1">
            <div className="text-xs">Diketik CS: TJ {formatIDR(fa.tanda_jadi_amount_idr || 0)} · DP {formatIDR(fa.dp_amount_idr || 0)} · pelunasan {formatIDR(fa.pelunasan_amount_idr || 0)} dari {formatIDR(fa.total_amount_idr)}</div>
            <Input placeholder="Catatan verifikasi (opsional)" value={catatan} onChange={(e) => setCatatan(e.target.value)} disabled={!hidup} />
            <Button size="sm" onClick={verifikasi} disabled={!hidup || sibuk} data-testid={`fa-lama-verifikasi-${fa.fa_number}`}>Verifikasi angka</Button>
          </div>
        )}
        {!isProduksi && !isFinance && <span className="text-xs text-muted-foreground">—</span>}
      </td>
    </tr>
  );
}
